import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { UserRole, isUserRole } from 'app/core/auth/role-routes';
import { User } from 'app/core/user/user.types';
import { GLOBAL } from 'app/services/GLOBAL';
import { Observable, catchError, finalize, map, of, share } from 'rxjs';

type SessionUser = User & { role?: unknown };

/**
 * Fuente del rol del servidor (D53, D60, D61) y único escritor de user-type.
 * No depende de AuthService: AuthService -> UserService -> RoleService.
 */
@Injectable({ providedIn: 'root' })
export class RoleService {
    private _httpClient = inject(HttpClient);
    private _role: UserRole | null = null;
    private _loaded = false;
    private _epoch = 0;
    private _token = localStorage.getItem('accessToken');
    private _inFlight$: Observable<SessionUser | null> | null = null;

    /** La caché solo pertenece a las credenciales con las que se obtuvo. */
    get role(): UserRole | null {
        this._syncSession();
        return this._role;
    }

    role$(): Observable<UserRole | null> {
        this._syncSession();
        if (this._loaded) {
            return of(this._role);
        }

        return this.user$().pipe(
            map((user) => (user ? this._role : null)),
            // Los fallos no se memorizan ni borran la caché visual (D61).
            catchError(() => of(null))
        );
    }

    /** Comparte también la petición del login; no almacena datos personales. */
    user$(): Observable<SessionUser | null> {
        this._syncSession();
        if (this._inFlight$) {
            return this._inFlight$;
        }

        const epoch = this._epoch;
        this._inFlight$ = this._httpClient
            .get<SessionUser>(GLOBAL.urlBase + 'user')
            .pipe(
                map((user) => {
                    this._syncSession();
                    // Una respuesta anterior al logout/cambio de usuario no
                    // puede reponer ni el rol ni la identidad en setUser().
                    if (epoch !== this._epoch) {
                        return null;
                    }
                    this.store(user?.role);
                    return user;
                }),
                finalize(() => {
                    // Una petición antigua no elimina la nueva que la sustituyó.
                    if (epoch === this._epoch) {
                        this._inFlight$ = null;
                    }
                }),
                share()
            );
        return this._inFlight$;
    }

    /** Solo recibe un rol ya leído del servidor; null también es un resultado. */
    store(role: unknown): UserRole | null {
        this._syncSession();
        this._loaded = true;
        this._role = isUserRole(role) ? role : null;
        if (this._role) {
            localStorage.setItem('user-type', this._role);
        } else {
            localStorage.removeItem('user-type');
        }
        return this._role;
    }

    clear(): void {
        this._invalidate();
        localStorage.removeItem('user-type');
    }

    private _invalidate(): void {
        this._epoch++;
        this._role = null;
        this._loaded = false;
        this._inFlight$ = null;
    }

    private _syncSession(): void {
        const token = localStorage.getItem('accessToken');
        if (token !== this._token) {
            this._token = token;
            this._invalidate();
        }
    }
}
